import {
	type CoverageData,
	formatFirstYearCoverage,
	formatSecondYearCoverage,
	type TranslationFunction
} from '@/features/Quote/services/coverageUtils';
import type { QuoteItem } from '@/types/api';
import type { AppData, ApplicationData, OccupationalHealthServiceQuote, Quote } from '@/types/app';

export interface ProductEntry {
	key: string;
	label: string;
	/** The resolved selected quote, or null if not selected / not found. */
	selectedQuote: Quote | OccupationalHealthServiceQuote | QuoteItem | null;
	/** Annual premium in euros, or null if it cannot be computed. */
	premiumEur: number | null;
	/** Secondary premium detail, such as premium percentage or rate per employee. */
	premiumDetail: string | null;
}

export const formatCurrency = (amount: number): string =>
	`€ ${Math.round(amount).toLocaleString('nl-NL')}`;

export const formatCurrencyWithDecimals = (amount: number): string =>
	`€ ${amount.toLocaleString('nl-NL', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2
	})}`;

export const formatPremiumPercentage = (
	premiumRatePercentage: number | null | undefined
): string | null => {
	if (premiumRatePercentage == null) return null;
	return `${premiumRatePercentage.toFixed(2)}%`;
};

export const formatOccupationalHealthServiceRate = (
	quote: OccupationalHealthServiceQuote
): string | null => {
	const rate = Number.parseFloat(quote.rate);
	if (Number.isNaN(rate)) return null;
	return formatCurrencyWithDecimals(rate);
};

export const computeOccupationalHealthServicePremiumEur = (
	quote: OccupationalHealthServiceQuote,
	werknemerAantal: number | null | undefined
): number | null => {
	const rate = Number.parseFloat(quote.rate);
	if (Number.isNaN(rate) || werknemerAantal == null || werknemerAantal <= 0) return null;
	return rate * werknemerAantal;
};

/** Returns the insurer slug for logo display, regardless of quote type. */
export const getInsurerSlug = (
	quote: Quote | OccupationalHealthServiceQuote | QuoteItem
): string | null => {
	if ('insurer' in quote) return quote.insurer;
	if ('provider_slug' in quote) return (quote as OccupationalHealthServiceQuote).provider_slug;
	return null;
};

/** Returns the display name for the insurer/provider. */
export const getInsurerName = (
	quote: Quote | OccupationalHealthServiceQuote | QuoteItem
): string | null => {
	if ('insurer_name' in quote) return (quote as Quote | QuoteItem).insurer_name;
	if ('provider_name' in quote) return (quote as OccupationalHealthServiceQuote).provider_name;
	return null;
};

interface BuildProductEntriesOptions {
	relevantProductTypes: string[];
	applicationData: ApplicationData;
	appData: AppData | null | undefined;
	occupationalHealthServiceQuotes: OccupationalHealthServiceQuote[];
	wgaExtendedQuotes: QuoteItem[];
	wgaQuotes: QuoteItem[];
	wiaExcessQuotes: QuoteItem[];
	t: (key: string) => string;
}

export const buildProductEntries = ({
	relevantProductTypes,
	applicationData,
	appData,
	occupationalHealthServiceQuotes,
	wgaExtendedQuotes,
	wgaQuotes,
	wiaExcessQuotes,
	t
}: BuildProductEntriesOptions): ProductEntry[] => {
	const employees = appData?.premium_variables?.employees?.employee_list ?? [];
	const werknemerAantal =
		(appData?.current_policy?.werknemer_aantal as number | null | undefined) ??
		(employees.length > 0 ? employees.length : null);

	const entries: ProductEntry[] = [];

	// Verzuimverzekering (short term sick leave)
	const sickLeaveQuotes = appData?.quotes?.sick_leave_short_term ?? [];
	const selectedSickLeave =
		sickLeaveQuotes.find(
			(q) => q.quote_id === applicationData.selected_sick_leave_short_term_quote_id
		) ?? null;
	entries.push({
		key: 'sick_leave_short_term',
		label: t('productPackage.products.sickLeaveShortTerm'),
		selectedQuote: selectedSickLeave,
		premiumEur: selectedSickLeave?.total ?? null,
		premiumDetail: selectedSickLeave
			? formatPremiumPercentage(selectedSickLeave.premium_rate_percentage)
			: null
	});

	// Occupational health service
	if (relevantProductTypes.includes('occupational_health_service')) {
		const selectedOccupationalHealthService =
			occupationalHealthServiceQuotes.find(
				(q) => q.quote_id === applicationData.selected_occupational_health_service_quote_id
			) ?? null;
		entries.push({
			key: 'occupational_health_service',
			label: t('productPackage.products.occupationalHealthService'),
			selectedQuote: selectedOccupationalHealthService,
			premiumEur: selectedOccupationalHealthService
				? computeOccupationalHealthServicePremiumEur(
						selectedOccupationalHealthService,
						werknemerAantal
					)
				: null,
			premiumDetail: selectedOccupationalHealthService
				? formatOccupationalHealthServiceRate(selectedOccupationalHealthService)
				: null
		});
	}

	// WGA Hiaat Uitgebreid
	if (relevantProductTypes.includes('sick_leave_long_term_wga_gap_extended')) {
		const selectedWgaExtended =
			wgaExtendedQuotes.find(
				(q) => q.quote_id === applicationData.selected_wga_gap_extended_quote_id
			) ?? null;
		entries.push({
			key: 'sick_leave_long_term_wga_gap_extended',
			label: t('productPackage.products.wgaGapExtended'),
			selectedQuote: selectedWgaExtended,
			premiumEur: selectedWgaExtended?.total ?? null,
			premiumDetail: selectedWgaExtended
				? formatPremiumPercentage(selectedWgaExtended.premium_rate_percentage)
				: null
		});
	}

	// WGA Hiaat
	if (relevantProductTypes.includes('sick_leave_long_term_wga_gap')) {
		const selectedWga =
			wgaQuotes.find((q) => q.quote_id === applicationData.selected_wga_gap_quote_id) ?? null;
		entries.push({
			key: 'sick_leave_long_term_wga_gap',
			label: t('productPackage.products.wgaGap'),
			selectedQuote: selectedWga,
			premiumEur: selectedWga?.total ?? null,
			premiumDetail: selectedWga
				? formatPremiumPercentage(selectedWga.premium_rate_percentage)
				: null
		});
	}

	// WIA Excedent
	if (relevantProductTypes.includes('sick_leave_long_term_wia_excess')) {
		const selectedWia =
			wiaExcessQuotes.find((q) => q.quote_id === applicationData.selected_wia_excess_quote_id) ??
			null;
		entries.push({
			key: 'sick_leave_long_term_wia_excess',
			label: t('productPackage.products.wiaExcess'),
			selectedQuote: selectedWia,
			premiumEur: selectedWia?.total ?? null,
			premiumDetail: selectedWia
				? formatPremiumPercentage(selectedWia.premium_rate_percentage)
				: null
		});
	}

	return entries;
};

export type SickLeaveCoverage = CoverageData & { employer_costs?: number | null };

/**
 * One-line summary of the sick-leave coverage, e.g. "Wachtdagen: 20 · Dekking 1e
 * jaar: 100%". Shared by the customer's package overview and the request overview.
 *
 * The coverage formatters must be given an empty value of `''`, not `null`: they
 * fall back to a literal `' - '` for anything nullish, which is truthy and would
 * survive the filter below as an empty "Dekking 1e jaar: -" row.
 */
export const buildCoverageSummary = (
	coverage: SickLeaveCoverage | null | undefined,
	t: TranslationFunction
): string | null => {
	if (!coverage) return null;
	const firstYear = formatFirstYearCoverage(coverage, t, '');
	const secondYear = formatSecondYearCoverage(coverage, '');
	const parts = [
		coverage.waiting_period != null &&
			`${t('application.steps.step1.waitingDays')}: ${coverage.waiting_period}`,
		coverage.employer_costs != null &&
			`${t('application.steps.step1.employerCosts')}: ${coverage.employer_costs}%`,
		firstYear && `${t('application.steps.step1.coverageFirstYear')}: ${firstYear}`,
		secondYear && `${t('application.steps.step1.coverageSecondYear')}: ${secondYear}`
	].filter(Boolean);
	return parts.length > 0 ? parts.join(' · ') : null;
};
